import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { PricePrecedentLine } from "../app/components/domain/pricing/PricePrecedentPanel";
import type {
  CommercialExceptionsBoard,
  ExceptionCard,
  ExceptionFlag,
  ExceptionOutcomeKind,
} from "@shared-core/data/commercial-exceptions-types";
import {
  ageSeverity,
  cardAgeHours,
  flagLabel,
  formatAge,
  formatCmPct,
} from "@shared-core/domain/commercial-exceptions";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { Textarea } from "../app/components/primitives/textarea";
import { Input } from "../app/components/primitives/input";
import {
  CheckCircle2,
  Clock,
  Hourglass,
  MessageSquare,
  Settings2,
  Ticket,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { currentTime } from "@shared-core/domain/current-time";

// Flag badge tones by rule kind. Money-risk flags read hot, information flags
// read cool — the eye should land on margin problems first. Rules are data,
// so unknown kinds get the neutral default.
const FLAG_TONES: Record<string, string> = {
  below_pricing_model:
    "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
  low_margin: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
  zero_value:
    "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300",
  high_margin:
    "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300",
  high_value: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300",
  new_customer:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  reactivated_customer:
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
};
const DEFAULT_FLAG_TONE =
  "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300";

const AGE_TONES = {
  fresh: "text-muted-foreground",
  aging: "text-amber-600 dark:text-amber-400 font-medium",
  overdue: "text-red-600 dark:text-red-400 font-medium",
} as const;

function FlagBadge({ flag }: { flag: ExceptionFlag }) {
  return (
    <span
      title={flag.detail ?? undefined}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${FLAG_TONES[flag.kind] ?? DEFAULT_FLAG_TONE}`}
    >
      {flagLabel(flag)}
    </span>
  );
}

function AgeBadge({ card, now }: { card: ExceptionCard; now: Date }) {
  const hours = cardAgeHours(card, now);
  if (hours == null) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs ${AGE_TONES[ageSeverity(hours)]}`}
    >
      <Clock className="h-3 w-3" aria-hidden />
      {formatAge(hours)}
    </span>
  );
}

function CmCell({ card }: { card: ExceptionCard }) {
  const pct = card.contribution_margin_pct;
  const tone =
    pct == null
      ? "text-muted-foreground"
      : pct < 0.2
        ? "text-red-600 dark:text-red-400"
        : pct > 0.4
          ? "text-violet-600 dark:text-violet-400"
          : "text-foreground";
  return <span className={`font-medium ${tone}`}>{formatCmPct(pct)}</span>;
}

function ClearDialog({
  card,
  outcomeKinds,
  onClose,
  onCleared,
}: {
  card: ExceptionCard | null;
  outcomeKinds: ExceptionOutcomeKind[];
  onClose: () => void;
  onCleared: () => void;
}) {
  const adapter = useDataAdapter();
  const [outcome, setOutcome] = useState(outcomeKinds[0]?.slug ?? "");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (card) {
      setOutcome(outcomeKinds[0]?.slug ?? "");
      setNote("");
    }
  }, [card, outcomeKinds]);

  const noteRequired =
    outcomeKinds.find((k) => k.slug === outcome)?.requires_note ?? false;
  const canSave =
    !saving && outcome !== "" && (!noteRequired || note.trim() !== "");

  const submit = async () => {
    if (!card) return;
    setSaving(true);
    const input = { outcome, note: note.trim() || undefined };
    try {
      // A ticketed card resolves (clearance + ticket closed); an untouched
      // card quick-clears.
      if (card.ticket_id) {
        await adapter.resolveException(card, card.ticket_id, input);
      } else {
        await adapter.quickClearException(card, input);
      }
      onCleared();
      onClose();
    } catch {
      toast.error("Could not clear the exception — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={card !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Clear exception</DialogTitle>
          <DialogDescription>
            {card ? `${card.estimate_header_ref} · ${card.customer_name}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="qc-outcome">
              Outcome
            </label>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger id="qc-outcome">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {outcomeKinds.map((kind) => (
                  <SelectItem key={kind.slug} value={kind.slug}>
                    {kind.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="qc-note">
              Note{noteRequired ? "" : " (optional)"}
            </label>
            <Textarea
              id="qc-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                noteRequired
                  ? "Required for this outcome"
                  : "e.g. standard rate for this account"
              }
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSave}>
            {saving ? "Clearing…" : "Clear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WaitingDialog({
  card,
  onClose,
  onMoved,
}: {
  card: ExceptionCard | null;
  onClose: () => void;
  onMoved: () => void;
}) {
  const adapter = useDataAdapter();
  const [waitingOn, setWaitingOn] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (card) setWaitingOn("");
  }, [card]);

  const submit = async () => {
    if (!card) return;
    setSaving(true);
    try {
      await adapter.updateExceptionState(card, "waiting", {
        waitingOn: waitingOn.trim() || undefined,
      });
      onMoved();
      onClose();
    } catch {
      toast.error("Could not move the card — try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={card !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Waiting on…</DialogTitle>
          <DialogDescription>
            Who or what is this review waiting for? Recorded on the linked
            ticket.
          </DialogDescription>
        </DialogHeader>
        <Input
          value={waitingOn}
          onChange={(e) => setWaitingOn(e.target.value)}
          placeholder="e.g. Henry Boyce — revised cost build-up"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? "Moving…" : "Move to waiting"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Dense triage row — the board's main working surface. */
function TriageRow({
  card,
  now,
  onQuickClear,
  onPickUp,
}: {
  card: ExceptionCard;
  now: Date;
  onQuickClear: () => void;
  onPickUp: () => void;
}) {
  return (
    <div className="border border-border rounded-lg p-3 bg-surface space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={card.estimate_url}
              className="font-medium text-primary hover:underline"
            >
              {card.estimate_header_ref}
            </Link>
            <span className="text-sm text-foreground truncate">
              {card.customer_name}
            </span>
            {card.customer_status && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                {card.customer_status === "new"
                  ? "New customer"
                  : "Reactivated"}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {card.job_type}
            </span>
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {card.description}
          </p>
          {/* What this customer last paid for the same spec — the question
              a reviewer actually has about a below-target quote
              (price-precedent brief J2). Absent only until the board
              composition carries it. */}
          {card.precedent && <PricePrecedentLine line={card.precedent} />}
        </div>
        <div className="text-right shrink-0">
          <div className="font-semibold">
            {card.estimate_amt === 0
              ? "£0"
              : formatCurrencyGbp(card.estimate_amt)}
          </div>
          <div className="text-sm">
            CM <CmCell card={card} />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          {card.flags.map((f) => (
            <FlagBadge key={f.alert_id} flag={f} />
          ))}
          <span className="text-xs text-muted-foreground">
            {card.estimator}
            {card.site ? ` · ${card.site}` : ""}
            {card.revision_count > 1 ? ` · rev ${card.revision_count}` : ""}
          </span>
          <AgeBadge card={card} now={now} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" variant="outline" onClick={onQuickClear}>
            <CheckCircle2 className="h-4 w-4 mr-1" aria-hidden />
            Fine as quoted
          </Button>
          <Button size="sm" onClick={onPickUp}>
            Pick up
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Compact card for the working rail (in review / waiting). */
function RailCard({
  card,
  now,
  onQuickClear,
  onWaiting,
  onBackToReview,
}: {
  card: ExceptionCard;
  now: Date;
  onQuickClear: () => void;
  onWaiting: () => void;
  onBackToReview: () => void;
}) {
  return (
    <div className="border border-border rounded-lg p-3 bg-surface space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Link
          to={card.estimate_url}
          className="font-medium text-primary hover:underline text-sm"
        >
          {card.estimate_header_ref}
        </Link>
        <span className="text-sm font-medium">
          {card.estimate_amt === 0
            ? "£0"
            : formatCurrencyGbp(card.estimate_amt)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground truncate">
        {card.customer_name}
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        {card.flags.map((f) => (
          <FlagBadge key={f.alert_id} flag={f} />
        ))}
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground flex items-center gap-2">
          {card.ticket_id && (
            <Link
              to={paths.ticketDetail({ id: card.ticket_id })}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <Ticket className="h-3 w-3" aria-hidden />
              Ticket
            </Link>
          )}
          {card.ticket_comment_count > 0 && (
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" aria-hidden />
              {card.ticket_comment_count}
            </span>
          )}
          <AgeBadge card={card} now={now} />
        </span>
        <span className="flex items-center gap-1">
          {card.state === "in_review" ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={onWaiting}
              title="Move to waiting"
            >
              <Hourglass className="h-4 w-4" aria-hidden />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              onClick={onBackToReview}
              title="Back to in review"
            >
              <Undo2 className="h-4 w-4" aria-hidden />
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onQuickClear}>
            Resolve
          </Button>
        </span>
      </div>
    </div>
  );
}

type FlagFilter = "all" | string;

// The board is a standing cockpit, not a page you open and close: it is meant
// to sit on a second monitor through the working day. Two consequences, both
// handled with the shared visibility-aware interval so a backgrounded tab
// costs nothing:
//   - ages must tick, or a card raised at 09:00 still reads "0m" at noon;
//   - the board must refetch, or the empty state's promise that exceptions
//     "appear within the hour" is only true if someone reloads. Evaluation
//     runs hourly, so a 5-minute poll surfaces a new flag promptly without
//     hammering the composed query.
const AGE_TICK_MS = 60_000;
const BOARD_REFRESH_MS = 5 * 60_000;

export function CommercialExceptionsPage() {
  const adapter = useDataAdapter();
  const { setVisibilityAwareInterval } = usePlatformCapabilities();
  const [board, setBoard] = useState<CommercialExceptionsBoard | null>(null);
  const [outcomeKinds, setOutcomeKinds] = useState<ExceptionOutcomeKind[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [flagFilter, setFlagFilter] = useState<FlagFilter>("all");
  const [clearCard, setClearCard] = useState<ExceptionCard | null>(null);
  const [waitingCard, setWaitingCard] = useState<ExceptionCard | null>(null);
  const [now, setNow] = useState(() => currentTime());

  // `silent` marks a background poll: it must never replace a board the user
  // is working in with the error screen, so a transient blip is swallowed and
  // the last good board stays on screen until a poll succeeds again.
  const load = useCallback(
    async (silent = false) => {
      if (!silent) setError(false);
      try {
        const [nextBoard, kinds] = await Promise.all([
          adapter.getCommercialExceptionsBoard(),
          adapter.getExceptionOutcomeKinds(),
        ]);
        setBoard(nextBoard);
        setOutcomeKinds(kinds);
        setError(false);
      } catch {
        if (!silent) setError(true);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [adapter],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Age badges are relative to `now`, so it has to advance on its own.
  useEffect(
    () => setVisibilityAwareInterval(() => setNow(currentTime()), AGE_TICK_MS),
    [setVisibilityAwareInterval],
  );

  // Pick up exceptions raised by the hourly evaluation without a reload.
  useEffect(
    () => setVisibilityAwareInterval(() => void load(true), BOARD_REFRESH_MS),
    [setVisibilityAwareInterval, load],
  );

  // Writes return receipts, not cards — the board refetches so counts, the
  // cleared rail, and the covered-alert rule all come from the server.
  const refresh = useCallback(() => void load(), [load]);

  const moveState = async (card: ExceptionCard, state: "in_review") => {
    try {
      await adapter.updateExceptionState(card, state);
      refresh();
    } catch {
      toast.error("Could not move the card — try again");
    }
  };

  const cards = useMemo(() => board?.cards ?? [], [board]);
  const flagKindsPresent = useMemo(() => {
    const kinds = new Set<string>();
    for (const c of cards) for (const f of c.flags) kinds.add(f.kind);
    return [...kinds];
  }, [cards]);

  const toReview = cards
    .filter((c) => c.state === "to_review")
    .filter(
      (c) => flagFilter === "all" || c.flags.some((f) => f.kind === flagFilter),
    )
    .sort((a, b) => (a.raised_at ?? "").localeCompare(b.raised_at ?? ""));
  const inReview = cards.filter((c) => c.state === "in_review");
  const waiting = cards.filter((c) => c.state === "waiting");
  const clearedToday = board?.cleared_today ?? [];

  if (loading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Loading commercial exceptions…
      </div>
    );
  }
  if (error || !board) {
    return (
      <div className="p-6 space-y-2">
        <p className="text-sm text-foreground font-medium">
          Could not load the exceptions board.
        </p>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header + completeness strip */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Commercial exceptions</h1>
          <p className="text-sm text-muted-foreground">
            Today: {board.today.quotes_total} quotes · {board.today.flagged}{" "}
            flagged · {board.today.cleared} cleared · {board.today.open} open
            {board.oldest_open_hours != null && (
              <>
                {" "}
                · oldest open{" "}
                <span
                  className={AGE_TONES[ageSeverity(board.oldest_open_hours)]}
                >
                  {formatAge(board.oldest_open_hours)}
                </span>
              </>
            )}
          </p>
        </div>
        <Link
          to={paths.settingsPricingTargets()}
          className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <Settings2 className="h-4 w-4" aria-hidden />
          Pricing targets
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Triage inbox — 2/3 */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-medium">
              To review{" "}
              <span className="text-muted-foreground">({toReview.length})</span>
            </h2>
            <div className="flex gap-1.5 flex-wrap">
              <button
                onClick={() => setFlagFilter("all")}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  flagFilter === "all"
                    ? "bg-primary text-white"
                    : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                }`}
              >
                All flags
              </button>
              {flagKindsPresent.map((kind) => (
                <button
                  key={kind}
                  onClick={() => setFlagFilter(kind)}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${
                    flagFilter === kind
                      ? "bg-primary text-white"
                      : "bg-surface border border-border text-foreground hover:bg-surface-hover"
                  }`}
                >
                  {flagLabel({ kind, title: kind })}
                </button>
              ))}
            </div>
          </div>
          {toReview.length === 0 ? (
            <div className="text-sm p-8 text-center border border-border rounded-lg">
              <p className="font-medium text-foreground">Nothing to review</p>
              <p className="text-muted-foreground">
                New exceptions appear here within the hour of a quote landing.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {toReview.map((card) => (
                <TriageRow
                  key={card.component_id}
                  card={card}
                  now={now}
                  onQuickClear={() => setClearCard(card)}
                  onPickUp={() => void moveState(card, "in_review")}
                />
              ))}
            </div>
          )}
        </div>

        {/* Working rail — 1/3 */}
        <div className="space-y-4">
          <section className="space-y-2">
            <h2 className="font-medium">
              In review{" "}
              <span className="text-muted-foreground">({inReview.length})</span>
            </h2>
            {inReview.length === 0 ? (
              <p className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-3">
                Nothing picked up.
              </p>
            ) : (
              inReview.map((card) => (
                <RailCard
                  key={card.component_id}
                  card={card}
                  now={now}
                  onQuickClear={() => setClearCard(card)}
                  onWaiting={() => setWaitingCard(card)}
                  onBackToReview={() => void moveState(card, "in_review")}
                />
              ))
            )}
          </section>
          <section className="space-y-2">
            <h2 className="font-medium">
              Waiting{" "}
              <span className="text-muted-foreground">({waiting.length})</span>
            </h2>
            {waiting.length === 0 ? (
              <p className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-3">
                Nothing waiting on anyone.
              </p>
            ) : (
              waiting.map((card) => (
                <RailCard
                  key={card.component_id}
                  card={card}
                  now={now}
                  onQuickClear={() => setClearCard(card)}
                  onWaiting={() => setWaitingCard(card)}
                  onBackToReview={() => void moveState(card, "in_review")}
                />
              ))
            )}
          </section>
          <section className="space-y-2">
            <h2 className="font-medium">
              Done today{" "}
              <span className="text-muted-foreground">
                ({clearedToday.length})
              </span>
            </h2>
            {clearedToday.length === 0 ? (
              <p className="text-xs text-muted-foreground border border-dashed border-border rounded-lg p-3">
                Nothing cleared yet.
              </p>
            ) : (
              <ul className="space-y-1">
                {clearedToday.map((clearance) => (
                  <li
                    key={`${clearance.component_id}-${clearance.cleared_at}`}
                    className="text-xs text-muted-foreground flex items-center gap-1.5"
                  >
                    <CheckCircle2
                      className="h-3.5 w-3.5 text-emerald-600 shrink-0"
                      aria-hidden
                    />
                    <span className="truncate">{clearance.subject_label}</span>
                    <span className="shrink-0 text-foreground">
                      · {clearance.outcome_label}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      <ClearDialog
        card={clearCard}
        outcomeKinds={outcomeKinds}
        onClose={() => setClearCard(null)}
        onCleared={refresh}
      />
      <WaitingDialog
        card={waitingCard}
        onClose={() => setWaitingCard(null)}
        onMoved={refresh}
      />
    </div>
  );
}
